import { isDefined } from "@clipboard-health/util-ts";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";

import { PullToRefresh } from "../../lib/PullToRefresh/PullToRefresh";
import { PlacementsPage } from "../Placements/Page";
import { useCreateOrGetPlacementCandidate } from "./api/useCreateOrGetPlacementCandidate";
import { useGetWorkerOpenPlacementFilters } from "./api/useGetWorkerOpenPlacementFilters";
import { PlacementCandidateError } from "./components/PlacementCandidateError";
import { PlacementCandidateSkeleton } from "./components/PlacementCandidateSkeleton";
import { getQualificationsString } from "./utils";

interface PlacementCandidatePageProps {
  worker: Worker;
}

export function PlacementCandidatePage(props: PlacementCandidatePageProps) {
  const { worker } = props;

  const { licensesData, shoppableQualifications } = useDefinedWorker();

  const {
    data: placementCandidate,
    isLoading: isPlacementCandidateLoading,
    isSuccess: isPlacementCandidateSuccess,
    refetch: refetchPlacementCandidate,
  } = useCreateOrGetPlacementCandidate(
    { workerId: worker.userId },
    { enabled: isDefined(worker.userId), staleTime: 60 * 1000 }
  );

  // Get worker qualifications for cold start filter reset
  const { data: allWorkerQualifications, isLoading: isLoadingWorkerQualifications } =
    useWorkerQualifications();

  const { data: workerTypesAvailableForHiring, isLoading: isLoadingWorkerTypes } =
    useGetWorkerOpenPlacementFilters({
      workerId: worker.userId,
      useHiringWorkerTypes: true,
    });

  // Build comma-separated string of worker qualification names
  const licenseQualificationNames = licensesData.map((license) => license.qualification);
  const shoppableQualificationIds = shoppableQualifications.map(
    (sq) => sq.relationships.qualification.data.id
  );

  const allWorkerQualificationsString = getQualificationsString({
    workerTypesAvailableForHiring,
    allWorkerQualifications,
    licenseQualificationNames,
    shoppableQualificationIds,
  });

  if (isPlacementCandidateLoading || isLoadingWorkerTypes || isLoadingWorkerQualifications) {
    return <PlacementCandidateSkeleton />;
  }

  if (!isPlacementCandidateSuccess || !isDefined(placementCandidate.id)) {
    return (
      <>
        <PullToRefresh onRefresh={refetchPlacementCandidate} />
        <PlacementCandidateError />
      </>
    );
  }

  return (
    <PlacementsPage
      placementCandidate={placementCandidate}
      worker={worker}
      allWorkerQualificationsString={allWorkerQualificationsString}
      workerTypesAvailableForHiring={workerTypesAvailableForHiring}
    />
  );
}
